import React, { useContext } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { ThemeProvider } from '@alefeducation/ui-elements-library-core';
import {
  studentListFilters,
  getUnassignedFilterKey,
} from '../../studentsListFilter';
import AssignmentContext from '../../../common/AssignmentContext/AssignmentContext';
import Spinner from '../../../common/Spinner/Spinner';
import Teams from '../../Teams';
import ConfirmationModal from '../../../common/ConfirmationModal/ConfirmationModal';
import { useLanguage } from '../../../../utils/language';
import { isNcleAlefAssignment } from '../../../../utils/isNcleAlefAssignment';
import { useStudentSubmissionState } from '../../../AssignmentSubmissionEvaluate/StudentSubmissionPageContext';
import { useStudentListContext } from '../StudentListContext';
import RequestResubmissionModal from '../../../AssignmentSubmissionEvaluate/RequestResubmissionModal';
import { NewUnassignedSection } from './NewUnassignedSection';
import { NewPendingSubmissionSection } from './NewPendingSubmissionSection';
import { NewPendingEvaluationSection } from './NewPendingEvaluationSection';
import { NewPendingReturnSubmissionsSection } from './NewPendingReturnSubmissionsSection';
import { NewReturnedSubmissionsSection } from './NewReturnedSubmissionsSection';
import { NewLeftTheClassSection } from './NewLeftTheClassSection';

const ListContainer = styled.div`
  flex: 1;
`;

const TeamsContainer = styled.div``;

const Container = styled.div`
  position: relative;
  background-color: white;
  border-radius: 10px;
  padding: 24px 0;
  margin-top: 30px;
  display: flex;
  width: 100%;
`;

export const NewAssignmentStudentsList = () => {
  const { t } = useTranslation();
  const {
    filters: { renderListFilters, selectedFilter },
    assignmentData: { isLoadingResults, assignmentType },
    assignmentParams: {
      assignmentId,
      assignmentInstanceId,
      mloId,
      contentId,
      classId,
      isNcle,
    },
    methods: { refetchClassAssignmentList },
  } = useContext(AssignmentContext);

  const {
    teamsData,
    isFailedLockModalOpen,
    setIsFailedLockModalOpen,
    setRefetchingClassAssignmentListInProgress,
    isLockingOfAssignmentEnabled,
    isStudentsWhoLeftTheClassEnabled,
  } = useStudentListContext();

  const { isRequestResubmissionModalOpen } = useStudentSubmissionState();

  const { langCode } = useLanguage();

  const showAllSections = selectedFilter.key === studentListFilters[0].key;
  const selectedFilterKey = selectedFilter.key.toUpperCase();
  const unassignedFilterKey = getUnassignedFilterKey(
    isNcleAlefAssignment(isNcle, assignmentType),
  ).toUpperCase();

  const refreshClassAssignmentList = async () => {
    setIsFailedLockModalOpen(false);
    setRefetchingClassAssignmentListInProgress(true);
    await refetchClassAssignmentList({
      input: {
        type: assignmentType,
        assignmentId,
        referrerId: assignmentInstanceId || mloId,
        classId,
        contentId,
        lessonId: mloId,
      },
    });
    setRefetchingClassAssignmentListInProgress(false);
  };

  const LATE_SUBMISSIONS = 'LATE SUBMISSIONS';

  return (
    <ThemeProvider>
      <Container>
        {!!teamsData.length && (
          <TeamsContainer>
            <Teams teamsData={teamsData} />
          </TeamsContainer>
        )}
        <ListContainer data-testid="dashboard-student-list">
          {isLoadingResults ? (
            <Spinner medium testId="assignmentSpinner" />
          ) : (
            <>
              {renderListFilters}

              {(showAllSections ||
                selectedFilterKey === 'PENDING SUBMISSION') && (
                <NewPendingSubmissionSection />
              )}
              {(showAllSections ||
                (selectedFilterKey === 'PENDING EVALUATION' ||
                  selectedFilterKey === LATE_SUBMISSIONS)) && (
                <NewPendingEvaluationSection />
              )}
              {(showAllSections ||
                selectedFilterKey === 'TO BE RETURNED' ||
                selectedFilterKey === LATE_SUBMISSIONS) && (
                <NewPendingReturnSubmissionsSection />
              )}
              {(showAllSections ||
                selectedFilterKey === 'RETURNED' ||
                selectedFilterKey === LATE_SUBMISSIONS) && (
                <NewReturnedSubmissionsSection />
              )}
              {isStudentsWhoLeftTheClassEnabled &&
                (showAllSections || selectedFilterKey === 'LEFT THE CLASS') && (
                  <NewLeftTheClassSection />
                )}
              {isLockingOfAssignmentEnabled &&
                (showAllSections ||
                  selectedFilterKey === unassignedFilterKey) && (
                  <NewUnassignedSection />
                )}
            </>
          )}
        </ListContainer>
        {isFailedLockModalOpen && (
          <ConfirmationModal
            testId="assignment-lock-error-prompt"
            lang={langCode}
            loading={false}
            onConfirm={refreshClassAssignmentList}
            title={t('Alert')}
            buttonTextConfirm="refresh"
          >
            <span>
              {t(
                'An update has been made to one of the student records. Please refresh the page before proceeding.',
              )}
            </span>
          </ConfirmationModal>
        )}
        {isRequestResubmissionModalOpen && <RequestResubmissionModal />}
      </Container>
    </ThemeProvider>
  );
};
